import { ReactNode } from 'react';
import { Controls, VideoPlayer } from '@deaf-bible-society-2-0/deafbible-video-player';

import useWatchPlanVideoTeam from '../../hooks/useWatchPlanVideoTeam';
import { WatchPlan, WatchPlanDay } from '../../utils/api/getWatchPlan';
import WatchPlanVideoTeamBar from './WatchPlanVideoTeamBar';
import styles from './styles.module.css';

interface Props {
  plan: WatchPlan;
  selectedDay: WatchPlanDay;
  darkMode: boolean;
  isMobile: boolean;
  translationCode?: string;
  dayList?: ReactNode;
  resources?: ReactNode;
}

/**
 * Pick a playable stream URL; prefer HLS on Safari / iOS.
 */
const getDayVideoSrc = (day: WatchPlanDay): string | undefined => {
  const { hls, dash } = day.video.video_url;
  const preferHls = /iPhone|iPad|iPod|Safari/i.test(navigator.userAgent);

  if (preferHls) {
    return hls || dash;
  }

  return hls || dash;
};

/**
 * Swap the last path segment of the banner URL to mobile or public.
 *
 * @param {string} bannerUrl - Banner URL from the API (often ends in /mobile or /public).
 * @param {boolean} isMobileDevice - Whether to use the mobile image variant.
 * @returns {string} Banner URL with the correct size segment.
 */
const getBannerUrlForDevice = (bannerUrl: string, isMobileDevice: boolean): string => {
  const variant = isMobileDevice ? 'mobile' : 'public';

  try {
    const url = new URL(bannerUrl);
    const segments = url.pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      return bannerUrl;
    }

    segments[segments.length - 1] = variant;
    url.pathname = `/${segments.join('/')}`;
    return url.toString();
  } catch {
    return bannerUrl.replace(/\/(mobile|public)\/?$/i, `/${variant}`);
  }
};

/**
 * Main watch plan content: banner, titles, selected day details, and video player.
 */
const WatchPlanMain = ({
  plan,
  selectedDay,
  darkMode,
  isMobile,
  translationCode,
  dayList,
  resources,
}: Props): JSX.Element => {
  const videoSrc = getDayVideoSrc(selectedDay);
  const bannerSrc = plan.banner_url ? getBannerUrlForDevice(plan.banner_url, isMobile) : undefined;
  const { team, qualityChecks } = useWatchPlanVideoTeam(translationCode, selectedDay.video.id);

  return (
    <div className={styles.mainPanel}>
      <div className={`${styles.planIntro} ${isMobile && styles.planIntroMobile}`}>
        {bannerSrc && (
          <img
            className={`${styles.banner} ${darkMode && styles.bannerDarkMode}`}
            src={bannerSrc}
            alt=""
            crossOrigin="anonymous"
          />
        )}

        <div className={styles.planHeader}>
          <h1 className={`${styles.planTitle} ${darkMode && styles.planTitleDarkMode}`}>
            {plan.plan_title}
          </h1>

          {plan.plan_short_description && (
            <p
              className={`${styles.planDescription} ${darkMode && styles.planDescriptionDarkMode}`}
            >
              {plan.plan_short_description}
            </p>
          )}
        </div>
      </div>

      <div
        className={`${styles.dayDivider} ${darkMode && styles.dayDividerDarkMode}`}
        role="separator"
        aria-label={`Day ${selectedDay.day_order}`}
      >
        <span className={styles.dayDividerLine} />
        <span className={styles.dayDividerLabel}>{`Day ${selectedDay.day_order}`}</span>
        <span className={styles.dayDividerLine} />
      </div>

      {dayList}

      <div className={styles.dayVideoSection}>
        <div className={styles.selectedDayRow}>
          <div className={styles.selectedDayBlock}>
            <p
              className={`${styles.selectedDayDescription} ${
                darkMode && styles.selectedDayDescriptionDarkMode
              }`}
            >
              {selectedDay.day_description}
            </p>
            {selectedDay.video.title && (
              <p className={`${styles.videoTitle} ${darkMode && styles.videoTitleDarkMode}`}>
                {selectedDay.video.title}
              </p>
            )}
          </div>
          {resources}
        </div>

        <div className={`${styles.videoContainer} ${darkMode && styles.videoContainerDarkMode}`}>
          {videoSrc && (
            <VideoPlayer
              key={selectedDay.id}
              src={videoSrc}
              autoplay
              controls={Controls.fullControlBar}
            />
          )}
        </div>

        <WatchPlanVideoTeamBar
          team={team}
          qualityChecks={qualityChecks}
          darkMode={darkMode}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

export default WatchPlanMain;
